import { lazy, Suspense, type ReactNode } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { AuthProvider } from './auth/AuthContext'
import { ProtectedRoute } from './auth/ProtectedRoute'
import { ThemeProvider } from './theme/ThemeProvider'
import { AppShell } from './layout/AppShell'
import { useAuth } from './auth/AuthContext'

import { LoginPage } from './pages/auth/LoginPage'
import { EmailEntryPage } from './pages/auth/EmailEntryPage'
import { RegisterPage } from './pages/auth/RegisterPage'
import { VerifyOtpPage } from './pages/auth/VerifyOtpPage'
import { PasswordResetConfirmPage, PasswordResetRequestPage } from './pages/auth/PasswordResetPage'
import { ForceChangePasswordPage } from './pages/auth/ForceChangePasswordPage'
import { DashboardPage } from './pages/dashboard/DashboardPage'
import { ClientesPage } from './pages/clientes/ClientesPage'
import { ConfiguracionPage } from './pages/configuracion/ConfiguracionPage'
import { EmpresaPage } from './pages/empresa/EmpresaPage'
import { ReportesPage } from './pages/reportes/ReportesPage'
import { SuscripcionPage } from './pages/suscripcion/SuscripcionPage'
import { ConfirmacionPage } from './pages/confirmacion/ConfirmacionPage'
import { AdminDashboardPage } from './pages/admin/AdminDashboardPage'
import { AdminLoginPage } from './pages/admin/AdminLoginPage'
import { BroadcastPage } from './pages/admin/BroadcastPage'
import { AdminUsersPage } from './pages/admin/AdminUsersPage'
import { FinanzasPage } from './pages/admin/FinanzasPage'
import { PlanesPage } from './pages/admin/PlanesPage'
import { ServerConfigPage } from './pages/admin/ServerConfigPage'
import { PerfilPage } from './pages/perfil/PerfilPage'
import { Placeholder } from './pages/Placeholder'
import { LegalPublicPage } from './pages/legal/LegalPublicPage'

// Lazy: el editor del reporte carga @react-pdf/renderer (~1MB) — solo al abrir un reporte.
const ReporteFormPage = lazy(() =>
  import('./pages/reportes/ReporteFormPage').then((m) => ({ default: m.ReporteFormPage })),
)

// Lazy: LegalPage carga Tiptap (~250KB) — solo al editar documentos legales.
const LegalPage = lazy(() =>
  import('./pages/admin/LegalPage').then((m) => ({ default: m.LegalPage })),
)

/**
 * Guard para rutas exclusivas de owner.
 * Redirige al dashboard si el usuario autenticado es técnico.
 */
function OwnerRoute({ children }: { children: ReactNode }) {
  const { state } = useAuth()
  const location = useLocation()

  if (state.status === 'loading') return null

  if (state.status === 'authenticated' && state.user.role !== 'owner') {
    return <Navigate to="/dashboard" replace state={{ from: location.pathname }} />
  }

  return <>{children}</>
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <Routes>
            {/* Públicas */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<EmailEntryPage />} />
            <Route path="/verify-otp" element={<VerifyOtpPage />} />
            <Route path="/register/complete" element={<RegisterPage />} />
            <Route path="/password-reset" element={<PasswordResetRequestPage />} />
            <Route path="/password-reset/confirm" element={<PasswordResetConfirmPage />} />
            <Route path="/legal/:tipo" element={<LegalPublicPage />} />

            {/* Login del admin de plataforma — endpoint y tabla SEPARADOS del login de usuario. */}
            <Route path="/admin/login" element={<AdminLoginPage />} />

            {/* Callback público del banco vía n8n — §10.4 paso 5 */}
            <Route path="/confirmacion/:ref256" element={<ConfirmacionPage />} />

            {/* Cambio de contraseña forzado — requiere auth de usuario pero no AppShell */}
            <Route
              path="/change-password"
              element={
                <ProtectedRoute requireAudience="user">
                  <ForceChangePasswordPage />
                </ProtectedRoute>
              }
            />

            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* Portal de usuario — AppShell con sidebar usuario */}
            <Route
              element={
                <ProtectedRoute requireAudience="user">
                  <AppShell variant="user" />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/reportes" element={<ReportesPage />} />
              <Route
                path="/reportes/:id"
                element={
                  <Suspense fallback={<div className="content-stack"><p className="muted">Cargando editor de reporte…</p></div>}>
                    <ReporteFormPage />
                  </Suspense>
                }
              />
              <Route path="/clientes" element={<ClientesPage />} />

              {/* Empresa y Configuración — solo owner */}
              <Route
                path="/empresa"
                element={
                  <OwnerRoute>
                    <EmpresaPage />
                  </OwnerRoute>
                }
              />
              <Route
                path="/configuracion"
                element={
                  <OwnerRoute>
                    <ConfiguracionPage />
                  </OwnerRoute>
                }
              />

              <Route
                path="/suscripcion"
                element={
                  <OwnerRoute>
                    <SuscripcionPage />
                  </OwnerRoute>
                }
              />
              <Route path="/perfil" element={<PerfilPage />} />
            </Route>

            {/* Portal admin — AppShell variant admin (sidebar y estado independientes §5.9) */}
            <Route
              element={
                <ProtectedRoute requireAudience="admin">
                  <AppShell variant="admin" />
                </ProtectedRoute>
              }
            >
              <Route path="/admin" element={<AdminDashboardPage />} />
              <Route path="/admin/broadcast" element={<BroadcastPage />} />
              <Route path="/admin/usuarios" element={<AdminUsersPage />} />
              <Route path="/admin/finanzas" element={<FinanzasPage />} />
              <Route path="/admin/planes" element={<PlanesPage />} />
              <Route
                path="/admin/legal"
                element={
                  <Suspense fallback={<div className="content-stack"><p className="muted">Cargando editor legal…</p></div>}>
                    <LegalPage />
                  </Suspense>
                }
              />
              <Route path="/admin/server-config" element={<ServerConfigPage />} />
              <Route path="/admin/perfil" element={<Placeholder title="Perfil del administrador" milestone="futuro" />} />
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  )
}

function NotFoundPage() {
  return (
    <main className="page">
      <h1>404</h1>
      <p>La ruta solicitada no existe.</p>
    </main>
  )
}

export default App
